/**
 * app-home.js
 */

import React from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { 
  alpha,
  useMediaQuery,
  Box,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { parseISO, format, differenceInSeconds, addDays } from 'date-fns';
import { UserContext } from 'contexts/user/user-context';
import { AppBarContext } from 'contexts/app-bar/app-bar-context';
import useAsyncInterval from 'utils/use-async-interval';
import { formatSecondsAsTime } from 'utils/datetime';
import PageTitle from 'components/page-title';
import FeatureBox from 'components/feature-box';
import Icon from 'components/icon';
import CardLink from 'components/card-link';
import Popover from 'components/popover';
import BarButton from 'components/layout/app-bar/bar-button';
import { ReportTypeLabel } from 'routing/routes/records/record-list/report-dialog/report-constants';


const REFRESH_INTERVAL_MS = 10000;


const FILTER_TYPE = {
  ALL: 'all',
  DOWNLOADED: 'downloaded',
  VIEWED: 'viewed',
  FAILED7DAY: 'failed-7-day',
  FAILED30DAY: 'failed-30-day'
}


const JOB_STATUS_TYPE = {
  ACTIVE: 'active',
  QUEUED: 'queued',
  COMPLETE: 'complete',
};


const StatCards = (props) => {
  const { stats, filterType, onClickCard } = props;
  
  
  if (!stats) {
    return null;
  }

  return (
    <>
      <CardLink
        iconType='report-warning'
        number={<>
          {stats.last7days.fail}
          <Box sx={{ display: 'inline-flex', fontSize: '14px' }}>&nbsp;/{stats.last7days.all}</Box>
        </>}
        label='Failed Reports'
        secondaryLabel='last 7 days'
        component={Link}
        to={`/admin/report-monitor/${FILTER_TYPE.FAILED7DAY}`}
        onClick={onClickCard}
        isActive={filterType === FILTER_TYPE.FAILED7DAY}
      />
      <CardLink
        iconType='report-warning'
        number={<>
          {stats.last30days.fail}
          <Box sx={{ display: 'inline-flex', fontSize: '14px' }}>&nbsp;/{stats.last30days.all}</Box>
        </>}
        label='Failed Reports'
        secondaryLabel='last 30 days'
        component={Link}
        to={`/admin/report-monitor/${FILTER_TYPE.FAILED30DAY}`}
        onClick={() => onClickCard}
        isActive={filterType === FILTER_TYPE.FAILED30DAY}
      />
    </>
  );
}


const DesktopFilterToggle = (props) => {
  const { value, onChange } = props;
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });


  if (isMobile) {
    return null;
  }


  return (
    <ToggleButtonGroup
      exclusive
      value={value || FILTER_TYPE.ALL}
      onChange={(e, v) => {
        if (v) {
          onChange(v);
        }
      }}
      sx={{
        mb: 4,
        '& .MuiButtonBase-root': {
          minWidth: 180,
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: theme => alpha(theme.palette.sys.primaryContainer, 0.5),
          }
        },
        '& .MuiSvgIcon-root': {
          mr: 1.5
        }
      }}
    >
      <ToggleButton 
        size='small' 
        value={FILTER_TYPE.ALL}
        component={Link}
        to={`/admin/report-monitor`}
      >
        <Icon type='outlined-circle' />
        All
      </ToggleButton>
      <ToggleButton 
        size='small' 
        value={FILTER_TYPE.DOWNLOADED}
        component={Link}
        to={`/admin/report-monitor/${FILTER_TYPE.DOWNLOADED}`}
      >
        <Icon type='download' />
        Downloaded
      </ToggleButton>
      <ToggleButton 
        size='small' 
        value={FILTER_TYPE.VIEWED}
        component={Link}
        to={`/admin/report-monitor/${FILTER_TYPE.VIEWED}`}
      >
        <Icon type='view' />
        Viewed
      </ToggleButton>
    </ToggleButtonGroup>
  );
}


const MobileFilterDropdown = (props) => {
  const { filterType, stats, onClickCard } = props;


  return (
    <Popover
      id='report-monitor-mobile-filter'
      label={<BarButton><Icon type='filter-list' /></BarButton>}
      closeOnClick
    >
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        p: 1.5,
        '& > *:not(:first-of-type)': { mt: 1 } 
      }}>
        <StatCards stats={stats} filterType={filterType} onClickCard={onClickCard} />
        <CardLink
          iconType='outlined-circle'
          label='All'
          secondaryLabel='Last 100'
          component={Link}
          to={`/admin/report-monitor`}
          onClick={onClickCard}
          isActive={!filterType}
        />
        <CardLink
          iconType='download'
          label='Downloaded'
          secondaryLabel='Last 100'
          component={Link}
          to={`/admin/report-monitor/${FILTER_TYPE.DOWNLOADED}`}
          onClick={onClickCard}
          isActive={filterType === FILTER_TYPE.DOWNLOADED}
        />
        <CardLink
          iconType='view'
          label='Viewed'
          secondaryLabel='Last 100'
          component={Link}
          to={`/admin/report-monitor/${FILTER_TYPE.VIEWED}`}
          onClick={onClickCard}
          isActive={filterType === FILTER_TYPE.VIEWED}
        />
      </Box>
    </Popover>
  );
}


const ReportTable = (props) => {
  const {
    title,
    subTitle,
    tableTitles,
    jobStatusType,
    jobs
  } = props;
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });

  
  return (
    <>
      <Box sx={{
        display: 'flex',
        alignItems: 'baseline',
        mb: 1
      }}>
        <Typography variant='h6'>{title}</Typography>
        {subTitle && <Typography variant='subtitle2' sx={{ ml: 1, color: 'sys.onSurfaceVariant' }}>{subTitle}</Typography>}
      </Box>
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size='small'>
          <TableHead sx={{ backgroundColor: 'sys.surfaceContainerHighest', whiteSpace: 'nowrap' }}>
            <TableRow>
              {tableTitles.map(headTitle => (
                <TableCell key={headTitle}>
                  {headTitle}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody sx={{ '& .MuiTableCell-body': { whiteSpace: 'nowrap' }}}>
            {jobs.map(job => (
              <TableRow key={job.id}><JobRow jobStatusType={jobStatusType} {...job} /></TableRow>
            ))}
            {jobs.length === 0 &&
            <TableRow>
              <TableCell 
                colSpan={99} 
                sx={{ 
                  textAlign: isMobile ? 'left' : 'center', 
                  py: 1, 
                  color: 'sys.onSurfaceVariant', 
                  fontSize: 'sizes.1' 
                }}
                children={`No reports ${title.toLowerCase()}`}
              />
            </TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}


const JobCellDate = (props) => {
  const { date } = props;
  const dateFormat = 'yyyy-MM-dd HH:mm:ss';

  return (
    <TableCell>{format(parseISO(date), dateFormat)}</TableCell>
  )
}


const JobRow = (props) => {
  const {
    id,
    jobStatusType,
    isExported,
    user,
    reportType,
    recordCount,
    dateCreated,
    dateStarted,
    dateCompleted,
    estimatedDuration,
    isSuccess,
  } = props;


  return (
    <>
      <TableCell>{id}</TableCell>
      {jobStatusType !== jobStatusType.QUEUED && <TableCell sx={{ position: 'relative' }}>
        <Icon 
          type={isExported ? 'download' : 'view'} 
          fontSize='small' 
          sx={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)' }} 
        />  
      </TableCell>}
      <TableCell>{user.name}</TableCell>
      <TableCell>{ReportTypeLabel[reportType]}</TableCell>
      <JobCellDate date={dateCreated} />

      {/* date started (active & complete) */}
      {[JOB_STATUS_TYPE.ACTIVE, JOB_STATUS_TYPE.COMPLETE].includes(jobStatusType) && <JobCellDate date={dateStarted} />}

      {/* date completed */}
      {jobStatusType === JOB_STATUS_TYPE.COMPLETE && <JobCellDate date={dateCompleted} />}

      {/* record count */}
      <TableCell>{recordCount}</TableCell>

      {/* duration (active) */}
      {jobStatusType === JOB_STATUS_TYPE.ACTIVE && <TableCell>{formatSecondsAsTime(differenceInSeconds(new Date(), dateStarted))}</TableCell>}

      {/* duration (complete) */}
      {jobStatusType === JOB_STATUS_TYPE.COMPLETE && <TableCell>{formatSecondsAsTime(differenceInSeconds(dateCompleted, dateStarted))}</TableCell>}

      {/* estimated duration */}
      <TableCell>{formatSecondsAsTime(estimatedDuration)}</TableCell>

      {/* success */}
      {jobStatusType === JOB_STATUS_TYPE.COMPLETE && <TableCell sx={{ position: 'relative' }}>
        <Icon 
          type={isSuccess ? 'checkmark' : 'close'} 
          className={isSuccess ? 'icon-complete' : 'icon-error'} 
          sx={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)' }} 
        />
      </TableCell>}
    </>
  )
}


export default function ReportMonitor() {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const { filterType } = useParams();
  const { admin } = React.useContext(UserContext);
  const { setAppBar } = React.useContext(AppBarContext);
  const [stats, setStats] = React.useState();
  const [jobs, setJobs] = React.useState();
  const isFilterForFailed = filterType === FILTER_TYPE.FAILED7DAY || filterType === FILTER_TYPE.FAILED30DAY;
  const title = 'Report Monitor';


  const updateStats = React.useCallback(async () => {
    const response = await axios.get(`/records/report-monitor-stats`);
    if (response.isSuccess) {
      setStats(response.data);
    }
  }, []);


  const updateJobs = React.useCallback(async (filterType) => {
    const isExported = filterType === FILTER_TYPE.DOWNLOADED
      ? true
      : filterType === FILTER_TYPE.VIEWED
        ? false
        : '';
    const isSuccess = filterType === FILTER_TYPE.FAILED7DAY || filterType === FILTER_TYPE.FAILED30DAY
      ? false
      : '';
    const startDate = filterType === FILTER_TYPE.FAILED7DAY
      ? addDays(new Date(), -7)
      : filterType === FILTER_TYPE.FAILED30DAY
        ? addDays(new Date(), -30)
        : '';
    const response = await axios.get(`/records/report-monitor-jobs`, { params: { isExported, isSuccess, startDate }});
    if (response.isSuccess) {
      setJobs(response.data);
    }
  }, []);


  useAsyncInterval(updateStats, REFRESH_INTERVAL_MS, true);
  useAsyncInterval(updateJobs, REFRESH_INTERVAL_MS, true, filterType);


  React.useEffect(() => {
    document.title = `PACKTRACK - ${title}`;
    setAppBar(title, [<MobileFilterDropdown filterType={filterType} stats={stats} onClickCard={() => setJobs()} />]);
  }, [title, filterType, stats, setAppBar]);
  

  if (!admin) {
    return null;
  }


  return (
    <Box sx={{
      py: 4,
      px: 2
    }}>
      {!isMobile &&
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <PageTitle iconName='todo' title={title} />
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: -1,
          '& > *:not(:first-of-type)': { ml: 1 } 
        }}>
          <StatCards stats={stats} filterType={filterType} onClickCard={() => setJobs()} />
        </Box>
      </Box>}
      
      <DesktopFilterToggle value={filterType} onChange={() => setJobs()} />

      <FeatureBox spinnerSize={60} sx={{ minHeight: 200 }} loading={!jobs}>

        {jobs &&
        <>
          {!isFilterForFailed &&
          <ReportTable
            title='In Progress'
            tableTitles={['Job', 'View Type', 'User', 'Report Type', 'Created', 'Started', 'Records', 'Duration', 'Estimate']}
            jobStatusType={JOB_STATUS_TYPE.ACTIVE}
            jobs={jobs.activeJobs}
          />}

          {(!filterType || filterType === FILTER_TYPE.DOWNLOADED) &&
          <ReportTable
            title='Queued'
            tableTitles={['Job', 'User', 'Report Type', 'Created', 'Records', 'Estimate']}
            jobStatusType={JOB_STATUS_TYPE.QUEUED}
            jobs={jobs.queuedJobs}
          />}

          <ReportTable
            title={isFilterForFailed
              ? 'Failed'
              : 'Complete'}
            subTitle={filterType === FILTER_TYPE.FAILED7DAY
              ? 'Last 7 Days'
              : filterType === FILTER_TYPE.FAILED30DAY
                ? 'Last 30 Days'
                : 'Last 100'}
            tableTitles={['Job', 'View Type', 'User', 'Report Type', 'Created', 'Started', 'Completed', 'Records', 'Duration', 'Estimate', 'Success']}
            jobStatusType={JOB_STATUS_TYPE.COMPLETE}
            jobs={jobs.completedJobs}
          />
        </>}

      </FeatureBox>
    </Box>
  )
}